import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';
import { 
  Plus, Search, Filter, Heart, Bookmark, Eye, Grid3X3, 
  Droplets, Utensils, Sun, Gem, Trees, Crown, Factory, 
  Flame, Snowflake, Folder, Lock, Globe, Share2, X, Image as ImageIcon
} from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CATEGORY_ICONS = {
  bathroom_spa: Droplets,
  modern_kitchen: Utensils,
  mediterranean: Sun,
  minimal_luxury: Gem,
  outdoor_living: Trees,
  classic_elegance: Crown,
  industrial_chic: Factory,
  warm_tones: Flame,
  cool_tones: Snowflake,
  other: Folder
};

const VISIBILITY_ICONS = {
  private: Lock,
  shared: Share2,
  public: Globe
};

function CollectionCard({ collection, onLike, onSave, currentUserId }) {
  const CategoryIcon = CATEGORY_ICONS[collection.category] || Folder;
  const VisibilityIcon = VISIBILITY_ICONS[collection.visibility] || Lock;
  
  return (
    <Link 
      to={`/collections/${collection.collection_id}`}
      className="group bg-white border border-neutral-200 overflow-hidden hover:shadow-lg transition-all duration-300"
      data-testid={`collection-card-${collection.collection_id}`}
    >
      {/* Cover Image */}
      <div className="aspect-[4/3] bg-neutral-100 relative overflow-hidden">
        {collection.cover_image ? (
          <img 
            src={collection.cover_image} 
            alt={collection.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <CategoryIcon className="w-16 h-16 text-neutral-300" />
          </div>
        )}
        
        {/* Visibility Badge */}
        <div className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm px-2 py-1 flex items-center gap-1.5 text-xs">
          <VisibilityIcon className="w-3 h-3" />
          <span className="capitalize">{collection.visibility}</span>
        </div>
        
        {/* Stats Overlay */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
          <div className="flex items-center gap-4 text-white text-sm">
            <span>{collection.materials_count} materiali</span>
            <span>{collection.images_count} immagini</span>
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="font-medium text-neutral-900 group-hover:text-neutral-600 transition-colors line-clamp-1">
            {collection.name}
          </h3>
          <div className="flex items-center gap-1 flex-shrink-0">
            <CategoryIcon className="w-4 h-4 text-neutral-400" />
          </div>
        </div>
        
        {collection.description && (
          <p className="text-sm text-neutral-500 line-clamp-2 mb-3">
            {collection.description}
          </p>
        )}
        
        {/* Tags */}
        {collection.tags && collection.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {collection.tags.slice(0, 3).map((tag, i) => (
              <span key={i} className="text-xs bg-neutral-100 text-neutral-600 px-2 py-0.5">
                #{tag}
              </span>
            ))}
          </div>
        )}
        
        {/* Footer */}
        <div className="flex items-center justify-between pt-3 border-t border-neutral-100">
          <span className="text-xs text-neutral-400">
            di {collection.user_name}
          </span>
          <div className="flex items-center gap-3">
            <button
              onClick={(e) => {
                e.preventDefault();
                onLike(collection.collection_id);
              }}
              className={`flex items-center gap-1 text-sm ${collection.is_liked ? 'text-red-500' : 'text-neutral-400 hover:text-red-500'}`}
              data-testid={`like-btn-${collection.collection_id}`}
            >
              <Heart className={`w-4 h-4 ${collection.is_liked ? 'fill-current' : ''}`} />
              <span>{collection.likes_count || 0}</span>
            </button>
            <button
              onClick={(e) => {
                e.preventDefault();
                onSave(collection.collection_id);
              }}
              className={`flex items-center gap-1 text-sm ${collection.is_saved ? 'text-[var(--gold)]' : 'text-neutral-400 hover:text-[var(--gold)]'}`}
              data-testid={`save-btn-${collection.collection_id}`}
            >
              <Bookmark className={`w-4 h-4 ${collection.is_saved ? 'fill-current' : ''}`} />
            </button>
          </div>
        </div>
      </div>
    </Link>
  );
}

function CreateCollectionModal({ isOpen, onClose, onCreated, categories }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'other',
    tags: '',
    visibility: 'private',
    cover_image: ''
  });
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }
    
    setUploadingImage(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await axios.post(`${API}/uploads/image`, formDataUpload, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setFormData(prev => ({ ...prev, cover_image: response.data.url }));
      toast.success('Image uploaded');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Please enter a collection name');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/collections`, {
        ...formData,
        tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean)
      }, { withCredentials: true });
      
      toast.success('Collection created!');
      onCreated(response.data);
      onClose();
      setFormData({ name: '', description: '', category: 'other', tags: '', visibility: 'private', cover_image: '' });
    } catch (error) {
      toast.error('Failed to create collection');
    } finally {
      setLoading(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div 
        className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-medium">New Collection</h2>
          <button onClick={onClose} className="p-1 hover:bg-neutral-100">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Cover Image Upload */}
          <div>
            <label className="block text-sm font-medium mb-1">Cover Image</label>
            <div className="border-2 border-dashed border-neutral-300 p-4 text-center">
              {formData.cover_image ? (
                <div className="relative">
                  <img 
                    src={formData.cover_image} 
                    alt="Cover" 
                    className="max-h-40 mx-auto object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, cover_image: '' }))}
                    className="absolute top-2 right-2 p-1 bg-white rounded-full shadow"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    disabled={uploadingImage}
                  />
                  {uploadingImage ? (
                    <span className="text-neutral-500">Uploading...</span>
                  ) : (
                    <span className="text-neutral-500 hover:text-neutral-900">
                      Click to upload cover image
                    </span>
                  )}
                </label>
              )}
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={e => setFormData({...formData, name: e.target.value})}
              placeholder="e.g., Modern Spa Bathroom"
              className="w-full border border-neutral-300 px-3 py-2 focus:outline-none focus:border-neutral-900"
              data-testid="collection-name-input"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={e => setFormData({...formData, description: e.target.value})}
              placeholder="Describe your collection..."
              rows={3}
              className="w-full border border-neutral-300 px-3 py-2 focus:outline-none focus:border-neutral-900"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Category</label>
            <select
              value={formData.category}
              onChange={e => setFormData({...formData, category: e.target.value})}
              className="w-full border border-neutral-300 px-3 py-2 focus:outline-none focus:border-neutral-900"
            >
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Tags (comma separated)</label>
            <input
              type="text"
              value={formData.tags}
              onChange={e => setFormData({...formData, tags: e.target.value})}
              placeholder="spa, relax, marble"
              className="w-full border border-neutral-300 px-3 py-2 focus:outline-none focus:border-neutral-900"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">Visibility</label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { value: 'private', label: 'Private', icon: Lock },
                { value: 'shared', label: 'Shareable', icon: Share2 },
                { value: 'public', label: 'Public', icon: Globe }
              ].map(opt => {
                const Icon = opt.icon;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setFormData({...formData, visibility: opt.value})}
                    className={`flex flex-col items-center gap-1 p-3 border ${
                      formData.visibility === opt.value 
                        ? 'border-neutral-900 bg-neutral-50' 
                        : 'border-neutral-200 hover:border-neutral-400'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="text-xs">{opt.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
          
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-neutral-900 text-white py-3 hover:bg-neutral-800 disabled:opacity-50 transition-colors"
            data-testid="create-collection-submit"
          >
            {loading ? 'Creating...' : 'Create Collection'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function CollectionsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [collections, setCollections] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  
  // Filters
  const [activeTab, setActiveTab] = useState('all'); // all, my, saved
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/collections/categories`);
      setCategories(response.data.categories);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, []);
  
  const fetchCollections = useCallback(async () => {
    setLoading(true);
    try {
      let endpoint = `${API}/collections`;
      const params = new URLSearchParams();
      
      if (activeTab === 'my' && user) {
        params.append('my_collections', 'true');
      } else if (activeTab === 'saved' && user) {
        endpoint = `${API}/collections/user/saved`;
      } else if (!user) {
        endpoint = `${API}/collections/public`;
      }
      
      if (selectedCategory !== 'all') {
        params.append('category', selectedCategory);
      }
      if (searchQuery) {
        params.append('search', searchQuery);
      }
      
      const url = params.toString() ? `${endpoint}?${params}` : endpoint;
      const response = await axios.get(url, { withCredentials: true });
      setCollections(response.data.collections || []);
    } catch (error) {
      console.error('Error fetching collections:', error);
      if (error.response?.status === 401 && activeTab !== 'all') {
        setActiveTab('all');
      }
    } finally {
      setLoading(false);
    }
  }, [activeTab, selectedCategory, searchQuery, user]);
  
  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);
  
  useEffect(() => {
    fetchCollections();
  }, [fetchCollections]);
  
  const handleLike = async (collectionId) => {
    if (!user) {
      toast.error('Please log in to like');
      return;
    }
    
    const collection = collections.find(c => c.collection_id === collectionId);
    try {
      if (collection?.is_liked) {
        await axios.delete(`${API}/collections/${collectionId}/like`, { withCredentials: true });
      } else {
        await axios.post(`${API}/collections/${collectionId}/like`, {}, { withCredentials: true });
      }
      fetchCollections();
    } catch (error) {
      toast.error('Error');
    }
  };
  
  const handleSave = async (collectionId) => {
    if (!user) {
      toast.error('Please log in to save');
      return;
    }
    
    const collection = collections.find(c => c.collection_id === collectionId);
    try {
      if (collection?.is_saved) {
        await axios.delete(`${API}/collections/${collectionId}/save`, { withCredentials: true });
        toast.success('Removed from saved');
      } else {
        await axios.post(`${API}/collections/${collectionId}/save`, {}, { withCredentials: true });
        toast.success('Saved!');
      }
      fetchCollections();
    } catch (error) {
      toast.error('Error');
    }
  };
  
  const handleCollectionCreated = (newCollection) => {
    navigate(`/collections/${newCollection.collection_id}`);
  };
  
  return (
    <div className="min-h-screen bg-neutral-50 flex flex-col" data-testid="collections-page">
      <Header />
      
      {/* Main Content with padding for fixed header */}
      <div className="flex-1 pt-20">
        {/* Page Header */}
        <div className="bg-white border-b border-neutral-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
          {/* Breadcrumb */}
          <Breadcrumb currentPage="Collections" />
          
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-light text-neutral-900">Collections</h1>
              <p className="text-neutral-500 mt-1">
                Explore and create themed material collections
              </p>
            </div>
            {user && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2 bg-neutral-900 text-white px-4 py-2.5 hover:bg-neutral-800 transition-colors"
                data-testid="create-collection-btn"
              >
                <Plus className="w-4 h-4" />
                New Collection
              </button>
            )}
          </div>
          
          {/* Tabs */}
          <div className="flex items-center gap-6 mt-6 border-b border-neutral-200 -mb-px">
            <button
              onClick={() => setActiveTab('all')}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'all' 
                  ? 'border-neutral-900 text-neutral-900' 
                  : 'border-transparent text-neutral-500 hover:text-neutral-900'
              }`}
            >
              <Globe className="w-4 h-4 inline mr-1.5" />
              Public
            </button>
            {user && (
              <>
                <button
                  onClick={() => setActiveTab('my')}
                  className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'my' 
                      ? 'border-neutral-900 text-neutral-900' 
                      : 'border-transparent text-neutral-500 hover:text-neutral-900'
                  }`}
                >
                  <Grid3X3 className="w-4 h-4 inline mr-1.5" />
                  My Collections
                </button>
                <button
                  onClick={() => setActiveTab('saved')}
                  className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'saved' 
                      ? 'border-neutral-900 text-neutral-900' 
                      : 'border-transparent text-neutral-500 hover:text-neutral-900'
                  }`}
                >
                  <Bookmark className="w-4 h-4 inline mr-1.5" />
                  Saved
                </button>
              </>
            )}
          </div>
        </div>
      </div>
      
      {/* Filters */}
      <div className="bg-white border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search collections..."
                className="w-full pl-10 pr-4 py-2 border border-neutral-300 focus:outline-none focus:border-neutral-900"
                data-testid="search-collections-input"
              />
            </div>
            
            {/* Category Filter */}
            <div className="flex items-center gap-2 overflow-x-auto pb-2 sm:pb-0">
              <button
                onClick={() => setSelectedCategory('all')}
                className={`px-3 py-1.5 text-sm whitespace-nowrap transition-colors ${
                  selectedCategory === 'all'
                    ? 'bg-neutral-900 text-white'
                    : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                }`}
              >
                Tutte
              </button>
              {categories.slice(0, 5).map(cat => {
                const Icon = CATEGORY_ICONS[cat.id] || Folder;
                return (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 text-sm whitespace-nowrap transition-colors ${
                      selectedCategory === cat.id
                        ? 'bg-neutral-900 text-white'
                        : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {cat.name}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="bg-white border border-neutral-200 animate-pulse">
                <div className="aspect-[4/3] bg-neutral-200" />
                <div className="p-4 space-y-3">
                  <div className="h-5 bg-neutral-200 w-2/3" />
                  <div className="h-4 bg-neutral-200 w-full" />
                  <div className="h-4 bg-neutral-200 w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : collections.length === 0 ? (
          <div className="text-center py-16">
            <Grid3X3 className="w-16 h-16 mx-auto text-neutral-300 mb-4" />
            <h3 className="text-lg font-medium text-neutral-900 mb-2">
              {activeTab === 'my' ? 'Non hai ancora creato collezioni' :
               activeTab === 'saved' ? 'Non hai salvato collezioni' :
               'Nessuna collezione trovata'}
            </h3>
            <p className="text-neutral-500 mb-6">
              {activeTab === 'my' 
                ? 'Crea la tua prima collezione per organizzare i materiali' 
                : 'Esplora le collezioni pubbliche e salvale per trovarle facilmente'}
            </p>
            {user && activeTab === 'my' && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center gap-2 bg-neutral-900 text-white px-4 py-2 hover:bg-neutral-800"
              >
                <Plus className="w-4 h-4" />
                Crea Collezione
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {collections.map(collection => (
              <CollectionCard
                key={collection.collection_id}
                collection={collection}
                onLike={handleLike}
                onSave={handleSave}
                currentUserId={user?.user_id}
              />
            ))}
          </div>
        )}
      </div>
      </div>
      
      {/* Create Modal */}
      <CreateCollectionModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={handleCollectionCreated}
        categories={categories}
      />
      
      <Footer />
    </div>
  );
}
